import { useCallback, useState } from 'react'
import type { TmsInvoiceRow, TmsInvoiceWorklist } from '@numeo/types-main-web-app'
import { fetchWorklistRows } from '../../api/worklistApi'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { showError } from '@/utils/snackbar'

/** Rows per part each "Show more" reads: the card's own first page size, so page 2 starts where the answer stopped. */
const MORE_PAGE_SIZE = 20

type ReadyPart = 'ready' | 'needs_documents'
const PARTS: ReadyPart[] = ['ready', 'needs_documents']

interface MoreWorklistRows {
    /** The rows read after the answer's own, in read order. */
    rows: TmsInvoiceRow[]
    /** Whether either Ready-tab part has rows not read yet. */
    hasMore: boolean
    loading: boolean
    loadMore: () => Promise<void>
}

/**
 * The answer's next rows (v17 table "Show more"): the next page of the worklist's Ready and needs-documents parts, read from the same
 * paged endpoint the Agent page's panel uses. Only for an answer with no delivery window: the endpoint pages every delivered load, so a
 * windowed answer would gain loads outside its period (`enabled` false). A failed read is shown and changes nothing.
 */
export const useMoreWorklistRows = (worklist: TmsInvoiceWorklist, enabled: boolean): MoreWorklistRows => {
    const [pages, setPages] = useState(1)
    const [rows, setRows] = useState<TmsInvoiceRow[]>([])
    const [loading, setLoading] = useState(false)
    const totals: Record<ReadyPart, number> = { ready: worklist.ready.total, needs_documents: worklist.needs_documents.total }
    const partHasMore = (part: ReadyPart) => totals[part] > pages * MORE_PAGE_SIZE
    const hasMore = enabled && PARTS.some(partHasMore)

    const loadMore = useCallback(async () => {
        if (!hasMore || loading) return
        setLoading(true)
        try {
            const next = pages + 1
            const read = await Promise.all(PARTS.filter(partHasMore).map((part) => fetchWorklistRows(part, { page: next, limit: MORE_PAGE_SIZE })))
            const fresh = read.flatMap((page) => page.rows).filter((row): row is TmsInvoiceRow => 'status_group' in row)
            setRows((current) => [...current, ...fresh])
            setPages(next)
        } catch (failure) {
            showError(getUserErrorMessage(failure))
        } finally {
            setLoading(false)
        }
        // partHasMore reads `pages` and the totals, both listed through `pages` and `worklist`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore, loading, pages, worklist])

    return { rows, hasMore, loading, loadMore }
}
